import { Inject, inject, Injectable, PLATFORM_ID, Renderer2, RendererFactory2 } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { filter } from 'rxjs';
import { LocalizedUrlService } from './localized-url.service';
import { LocaleService, SUPPORTED_LOCALES } from './locale.service';
import { environment } from '../../../environments/environment';

/** Attribut apposé sur tous les éléments <link> gérés par ce service */
const SEO_ATTR = 'data-seo';
const SEO_MANAGED = 'managed';

@Injectable({
  providedIn: 'root',
})
export class SeoService {
  private router = inject(Router);
  private localizedUrlService = inject(LocalizedUrlService);
  private localeService = inject(LocaleService);
  private platformId = inject(PLATFORM_ID);
  private renderer: Renderer2;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    rendererFactory: RendererFactory2,
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  init(): void {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event) => {
        const navEnd = event as NavigationEnd;
        const url = navEnd.urlAfterRedirects ?? navEnd.url;
        const locale = this.localizedUrlService.localeFromUrl(url);

        // Toujours nettoyer les balises gérées avant de réécrire
        this.removeManagedTags();

        if (!locale) {
          // Route non-publique (back-office / auth) : on ne publie pas d'alternates
          return;
        }

        // 1. <html lang>
        this.document.documentElement.lang = locale;

        // 2. Origin : préférer DOCUMENT.location.origin (fonctionne aussi en SSR),
        //    sinon fallback sur environment.appUrl, sinon chaîne vide.
        const origin = this.resolveOrigin();

        // 3. Canonical (URL courante)
        const canonicalHref = origin + url;
        this.addLink('canonical', canonicalHref);

        // 4. hreflang alternates pour chaque locale supportée
        for (const targetLocale of SUPPORTED_LOCALES) {
          const tree = this.localizedUrlService.equivalentUrl(targetLocale);
          const path = this.router.serializeUrl(tree);
          const href = origin + path;
          this.addAlternate(targetLocale, href);
        }

        // 5. x-default pointe sur la version française (locale principale)
        const defaultTree = this.localizedUrlService.equivalentUrl('fr');
        const defaultPath = this.router.serializeUrl(defaultTree);
        this.addAlternate('x-default', origin + defaultPath);
      });
  }

  // ---------------------------------------------------------------------------
  // Helpers privés
  // ---------------------------------------------------------------------------

  private resolveOrigin(): string {
    // DOCUMENT.location est disponible aussi côté serveur (Angular SSR injecte
    // une implémentation qui porte l'URL de la requête entrante).
    try {
      const origin = this.document.location?.origin;
      if (origin && origin !== 'null') {
        return origin;
      }
    } catch {
      // Certains environnements de test ne fournissent pas location
    }
    // Fallback : URL publique configurée (supprime le slash final)
    return environment.appUrl.replace(/\/$/, '');
  }

  private addLink(rel: string, href: string): void {
    const link: HTMLLinkElement = this.renderer.createElement('link');
    this.renderer.setAttribute(link, 'rel', rel);
    this.renderer.setAttribute(link, 'href', href);
    this.renderer.setAttribute(link, SEO_ATTR, SEO_MANAGED);
    this.renderer.appendChild(this.document.head, link);
  }

  private addAlternate(hreflang: string, href: string): void {
    const link: HTMLLinkElement = this.renderer.createElement('link');
    this.renderer.setAttribute(link, 'rel', 'alternate');
    this.renderer.setAttribute(link, 'hreflang', hreflang);
    this.renderer.setAttribute(link, 'href', href);
    this.renderer.setAttribute(link, SEO_ATTR, SEO_MANAGED);
    this.renderer.appendChild(this.document.head, link);
  }

  private removeManagedTags(): void {
    const managed = this.document.head.querySelectorAll(`[${SEO_ATTR}="${SEO_MANAGED}"]`);
    managed.forEach((el) => el.parentNode?.removeChild(el));
  }
}
